import { omit, last, first, mapValues, uniq, keys, flatten } from 'lodash';
import { unreachable } from '../../../../ts-utils';
import { addComentChunkToStart, addCommentChunkToEnd, addRepliesChunkToStart, addRepliesChunkToEnd, } from './comment-drop-utils';
import { FETCH_NEXT_COMMENTS_FAILURE, FETCH_NEXT_COMMENTS_REQUEST, FETCH_NEXT_COMMENTS_SUCCESS, FETCH_NEXT_REPLIES_FAILURE, FETCH_NEXT_REPLIES_REQUEST, FETCH_NEXT_REPLIES_SUCCESS, FETCH_PREV_COMMENTS_FAILURE, FETCH_PREV_COMMENTS_REQUEST, FETCH_PREV_COMMENTS_SUCCESS, FETCH_PREV_REPLIES_FAILURE, FETCH_PREV_REPLIES_REQUEST, FETCH_PREV_REPLIES_SUCCESS, VIEW_REPLY_TARGET_DELETED, VIEW_REPLY_TARGET_AVAILABLE, VIEW_REPLY_TARGET_REQUEST, VIEW_REPLY_TARGET_SUCCESS, VIEW_REPLY_TARGET_FAILURE, } from './comments-paging-actions';
import { constructScrollAnchorForComment, } from '../comments-types';
import { isReplyStateIN } from '../replies/reply-types';
import { getChildrenIds, getReplyListState } from '../replies/replies-selectors';
import { getVotesStateIN } from '../votes/comments-vote-utils';
import { hiddenComponentEffectsGuard } from '../effects/hidden-component-effects-guard';
export function commentsPagingReducer(state, action) {
    switch (action.type) {
        case FETCH_NEXT_COMMENTS_REQUEST: {
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    nextChunkState: { type: 'PENDING', pendingRequestId: action.payload.requestId },
                },
            };
        }
        case FETCH_NEXT_COMMENTS_SUCCESS: {
            const { requestId, response } = action.payload;
            if (state.topLevelCommentList.nextChunkState?.type !== 'PENDING' ||
                state.topLevelCommentList.nextChunkState.pendingRequestId !== requestId) {
                return state;
            }
            const [chunk, comments, replies] = commentFetchResponseToChunk(response);
            const { chunks, commentIdsToRemove } = addCommentChunkToEnd({
                chunks: state.topLevelCommentList.chunks,
                newChunk: chunk,
                pageLimit: state.configs.pagination.maxPagesBeforeDrop.commentPages,
                liveCommentsPurgatory: state.topLevelCommentList.liveCommentsPurgatory,
            });
            const replyIdsToRemove = flatten(commentIdsToRemove.map(getChildrenIds(state.commentsById)));
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    chunks,
                    nextChunkState: { type: 'IDLE' },
                },
                commentsById: mergeWithNewComments(omit(state.commentsById, [...commentIdsToRemove, ...replyIdsToRemove]), comments),
                replyLists: mergeWithNewReplies(omit(state.replyLists, commentIdsToRemove), replies),
                ...hiddenComponentEffectsGuard(state, {
                    scrollAnchor: constructScrollAnchorForComment(getLastCommentId(state.topLevelCommentList.chunks)),
                }),
            };
        }
        case FETCH_NEXT_COMMENTS_FAILURE: {
            const { requestId, message } = action.payload;
            if (state.topLevelCommentList.nextChunkState?.type !== 'PENDING' ||
                state.topLevelCommentList.nextChunkState?.pendingRequestId !== requestId) {
                return state;
            }
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    nextChunkState: { type: 'IDLE' },
                },
                ...hiddenComponentEffectsGuard(state, {
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case FETCH_PREV_COMMENTS_REQUEST: {
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    prevChunkState: { type: 'PENDING', pendingRequestId: action.payload.requestId },
                },
            };
        }
        case FETCH_PREV_COMMENTS_SUCCESS: {
            const { requestId, response } = action.payload;
            const { topLevelCommentList } = state;
            if (topLevelCommentList.prevChunkState?.type !== 'PENDING' ||
                topLevelCommentList.prevChunkState.pendingRequestId !== requestId) {
                return state;
            }
            const [chunk, comments, replies] = commentFetchResponseToChunk(response);
            const { chunks, commentIdsToRemove } = addComentChunkToStart({
                chunks: topLevelCommentList.chunks,
                newChunk: chunk,
                pageLimit: state.configs.pagination.maxPagesBeforeDrop.commentPages,
                liveCommentsPurgatory: state.topLevelCommentList.liveCommentsPurgatory,
            });
            const replyIdsToRemove = flatten(commentIdsToRemove.map(getChildrenIds(state.commentsById)));
            return {
                ...state,
                topLevelCommentList: {
                    ...state.topLevelCommentList,
                    chunks,
                    prevChunkState: { type: 'IDLE' },
                },
                commentsById: mergeWithNewComments(omit(state.commentsById, [...commentIdsToRemove, ...replyIdsToRemove]), comments),
                replyLists: mergeWithNewReplies(omit(state.replyLists, commentIdsToRemove), replies),
                ...hiddenComponentEffectsGuard(state, {
                    scrollAnchor: constructScrollAnchorForComment(getFirstCommentId(topLevelCommentList.chunks)),
                }),
            };
        }
        case FETCH_PREV_COMMENTS_FAILURE: {
            const { requestId, message } = action.payload;
            const { topLevelCommentList } = state;
            if (topLevelCommentList.prevChunkState?.type !== 'PENDING' ||
                topLevelCommentList.prevChunkState.pendingRequestId !== requestId) {
                return state;
            }
            return {
                ...state,
                topLevelCommentList: {
                    ...topLevelCommentList,
                    prevChunkState: { type: 'IDLE' },
                },
                ...hiddenComponentEffectsGuard(state, {
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case FETCH_NEXT_REPLIES_REQUEST: {
            const { commentId } = action.payload;
            return {
                ...state,
                replyLists: {
                    ...state.replyLists,
                    [commentId]: {
                        ...state.replyLists[commentId],
                        nextChunkState: { type: 'PENDING', pendingRequestId: action.payload.requestId },
                    },
                },
            };
        }
        case FETCH_NEXT_REPLIES_SUCCESS: {
            const { commentId } = action.payload;
            const { requestId, response } = action.payload;
            const commentRepliesState = state.replyLists[commentId];
            if (commentRepliesState.nextChunkState.type !== 'PENDING' ||
                commentRepliesState.nextChunkState.pendingRequestId !== requestId) {
                return state;
            }
            const [chunk, repliesById] = moreRepliesResponseToChunk({
                comments: response.comments.comments,
                pagingMetadata: response.comments.pagingMetadata,
                reactions: response.reactions,
                parentId: commentId,
            });
            const { chunks, replyIdsToRemove } = addRepliesChunkToEnd({
                chunks: commentRepliesState.chunks,
                newChunk: chunk,
                newReplies: commentRepliesState.newRepliesToReplies,
                pageLimit: state.configs.pagination.maxPagesBeforeDrop.replyPages,
                liveRepliesPurgatory: commentRepliesState.liveRepliesPurgatory,
            });
            return {
                ...state,
                commentsById: mergeWithNewComments(omit(state.commentsById, replyIdsToRemove), repliesById),
                replyLists: mapValues(state.replyLists, (repliesState, id) => {
                    if (id === commentId) {
                        // In this specific case we have to purge live replies, as they are loaded with pagination request
                        const isOnlyLoadMoreAndLiveState = getReplyListState(repliesState, state.commentsById)?.type ===
                            'ONLY_LOAD_MORE_AND_LIVE';
                        return {
                            ...repliesState,
                            newRepliesToReplies: omit(repliesState.newRepliesToReplies, replyIdsToRemove),
                            chunks,
                            nextChunkState: { type: 'IDLE' },
                            liveRepliesPurgatory: isOnlyLoadMoreAndLiveState
                                ? []
                                : repliesState.liveRepliesPurgatory,
                        };
                    }
                    return {
                        ...repliesState,
                        newRepliesToReplies: omit(repliesState.newRepliesToReplies, replyIdsToRemove),
                    };
                }),
                ...hiddenComponentEffectsGuard(state, {
                    scrollAnchor: constructScrollAnchorForComment(getLastReplyId(commentRepliesState.chunks)),
                }),
            };
        }
        case FETCH_NEXT_REPLIES_FAILURE: {
            const { commentId, requestId, message } = action.payload;
            const commentRepliesState = state.replyLists[commentId];
            if (!commentRepliesState ||
                commentRepliesState.nextChunkState?.type !== 'PENDING' ||
                commentRepliesState.nextChunkState?.pendingRequestId !== requestId) {
                return state;
            }
            return {
                ...state,
                replyLists: {
                    ...state.replyLists,
                    [commentId]: { ...commentRepliesState, nextChunkState: { type: 'IDLE' } },
                },
                ...hiddenComponentEffectsGuard(state, {
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case FETCH_PREV_REPLIES_REQUEST: {
            const { commentId } = action.payload;
            return {
                ...state,
                replyLists: {
                    ...state.replyLists,
                    [commentId]: {
                        ...state.replyLists[commentId],
                        prevChunkState: { type: 'PENDING', pendingRequestId: action.payload.requestId },
                    },
                },
            };
        }
        case FETCH_PREV_REPLIES_SUCCESS: {
            const { commentId } = action.payload;
            const { requestId, response } = action.payload;
            const commentRepliesState = state.replyLists[commentId];
            if (commentRepliesState.prevChunkState.type !== 'PENDING' ||
                commentRepliesState.prevChunkState.pendingRequestId !== requestId) {
                return state;
            }
            const [chunk, repliesById] = moreRepliesResponseToChunk({
                comments: response.comments.comments,
                pagingMetadata: response.comments.pagingMetadata,
                reactions: response.reactions,
                parentId: commentId,
            });
            const { chunks, replyIdsToRemove } = addRepliesChunkToStart({
                chunks: commentRepliesState.chunks,
                newChunk: chunk,
                newReplies: commentRepliesState.newRepliesToReplies,
                pageLimit: state.configs.pagination.maxPagesBeforeDrop.replyPages,
                liveRepliesPurgatory: commentRepliesState.liveRepliesPurgatory,
            });
            return {
                ...state,
                commentsById: mergeWithNewComments(omit(state.commentsById, replyIdsToRemove), repliesById),
                replyLists: mapValues(state.replyLists, (repliesState, id) => {
                    if (id === commentId) {
                        return {
                            ...repliesState,
                            newRepliesToReplies: omit(repliesState.newRepliesToReplies, replyIdsToRemove),
                            chunks,
                            prevChunkState: { type: 'IDLE' },
                        };
                    }
                    return {
                        ...repliesState,
                        newRepliesToReplies: omit(repliesState.newRepliesToReplies, replyIdsToRemove),
                    };
                }),
                ...hiddenComponentEffectsGuard(state, {
                    scrollAnchor: constructScrollAnchorForComment(getFirstReplyId(commentRepliesState.chunks)),
                }),
            };
        }
        case FETCH_PREV_REPLIES_FAILURE: {
            const { commentId, requestId, message } = action.payload;
            const commentRepliesState = state.replyLists[commentId];
            if (!commentRepliesState ||
                commentRepliesState.prevChunkState?.type !== 'PENDING' ||
                commentRepliesState.prevChunkState?.pendingRequestId !== requestId) {
                return state;
            }
            return {
                ...state,
                replyLists: {
                    ...state.replyLists,
                    [commentId]: { ...commentRepliesState, prevChunkState: { type: 'IDLE' } },
                },
                ...hiddenComponentEffectsGuard(state, {
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case VIEW_REPLY_TARGET_AVAILABLE: {
            return {
                ...state,
                ...hiddenComponentEffectsGuard(state, {
                    highlight: {
                        type: 'MARK_TARGET',
                        id: action.payload.targetCommentId,
                        timestamp: Date.now(),
                    },
                }),
            };
        }
        case VIEW_REPLY_TARGET_REQUEST: {
            const { sourceCommentId } = action.payload;
            const sourceComment = state.commentsById[sourceCommentId];
            if (sourceComment.type !== 'REPLY_READY' || sourceComment.replyingToTargetLoading) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [sourceCommentId]: { ...sourceComment, replyingToTargetLoading: true },
                },
            };
        }
        case VIEW_REPLY_TARGET_SUCCESS: {
            const { targetCommentId, sourceCommentId, comment, reactions } = action.payload;
            const sourceComment = state.commentsById[sourceCommentId];
            if (sourceComment.type !== 'REPLY_READY' || !sourceComment.replyingToTargetLoading) {
                return state;
            }
            const parentId = sourceComment.parentId;
            const repliesState = state.replyLists[parentId];
            if (state.commentsById[targetCommentId]) {
                // We already got the comment elsewhere, so only highlithing it and reseting loader
                return {
                    ...state,
                    commentsById: {
                        ...state.commentsById,
                        [sourceCommentId]: { ...sourceComment, replyingToTargetLoading: false },
                    },
                    ...hiddenComponentEffectsGuard(state, {
                        highlight: { type: 'MARK_TARGET', id: targetCommentId, timestamp: Date.now() },
                    }),
                };
            }
            const commentReactions = reactions?.[targetCommentId];
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [targetCommentId]: {
                        type: 'REPLY_READY',
                        comment,
                        commentId: comment.commentId,
                        parentId,
                        targetId: comment.replyTo.commentId,
                        targetStatus: comment.replyTo.status,
                        replyingToTargetLoading: false,
                        reactions: {
                            total: commentReactions?.total ?? 0,
                            reactions: commentReactions?.reactions ?? [],
                            type: 'IDLE',
                        },
                        marked: { type: 'IDLE' },
                    },
                    [sourceCommentId]: { ...sourceComment, replyingToTargetLoading: false },
                },
                replyLists: {
                    ...state.replyLists,
                    [parentId]: {
                        ...repliesState,
                        chunks: [...repliesState.chunks, { type: 'REPLY_TARGET', commentId: targetCommentId }],
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'MARK_TARGET', id: targetCommentId, timestamp: Date.now() },
                    scrollAnchor: constructScrollAnchorForComment(sourceCommentId),
                }),
            };
        }
        case VIEW_REPLY_TARGET_FAILURE: {
            const { sourceCommentId, message } = action.payload;
            const sourceComment = state.commentsById[sourceCommentId];
            if (sourceComment.type !== 'REPLY_READY' || !sourceComment.replyingToTargetLoading) {
                return state;
            }
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [sourceCommentId]: { ...sourceComment, replyingToTargetLoading: false },
                },
                ...hiddenComponentEffectsGuard(state, {
                    toast: message.type === 'TOAST'
                        ? { skin: 'error', message: message.message, timestamp: Date.now() }
                        : state.effects?.toast,
                }),
            };
        }
        case VIEW_REPLY_TARGET_DELETED: {
            const { sourceCommentId, targetCommentId, parentCommentId } = action.payload;
            const replyListState = state.replyLists[parentCommentId];
            return {
                ...state,
                commentsById: {
                    ...state.commentsById,
                    [targetCommentId]: {
                        type: 'REPLY_DELETED',
                        commentId: targetCommentId,
                        parentId: targetCommentId,
                        targetId: targetCommentId,
                        targetStatus: 'UNKNOWN',
                        replyingToTargetLoading: false,
                    },
                },
                replyLists: {
                    ...state.replyLists,
                    [parentCommentId]: {
                        ...replyListState,
                        chunks: [
                            ...replyListState.chunks,
                            { type: 'REPLY_TARGET', commentId: targetCommentId },
                        ],
                    },
                },
                ...hiddenComponentEffectsGuard(state, {
                    highlight: { type: 'MARK_TARGET', id: targetCommentId, timestamp: Date.now() },
                    scrollAnchor: constructScrollAnchorForComment(sourceCommentId),
                }),
            };
        }
        default:
            throw unreachable(action);
    }
}
const moreRepliesResponseToChunk = ({ comments, pagingMetadata, reactions, parentId, }) => {
    return [
        {
            type: 'FETCHED',
            commentIds: (comments || []).map((c) => c.commentId),
            nextCursor: pagingMetadata.nextCursor,
            previousCursor: pagingMetadata.previousCursor,
            total: pagingMetadata.count ?? 0,
        },
        (comments || []).reduce((acc, comment) => {
            const commentReactions = reactions[comment.commentId];
            acc[comment.commentId] = {
                type: 'REPLY_READY',
                comment,
                commentId: comment.commentId,
                parentId,
                targetId: comment.replyTo.commentId,
                targetStatus: comment.replyTo.status,
                replyingToTargetLoading: false,
                reactions: {
                    total: commentReactions?.total ?? 0,
                    reactions: commentReactions?.reactions ?? [],
                    type: 'IDLE',
                },
                marked: { type: 'IDLE' },
            };
            return acc;
        }, {}),
    ];
};
export const commentFetchResponseToChunk = (response) => {
    const { comments, votes, reactions } = response;
    const fetchedThunk = {
        type: 'FETCHED',
        commentIds: (comments.comments || []).map((c) => c.commentId),
        nextCursor: comments.pagingMetadata.nextCursor,
        previousCursor: comments.pagingMetadata.previousCursor,
        total: comments.pagingMetadata.count ?? 0,
    };
    const commentsById = comments.comments.reduce((acc, comment) => {
        const commentReactions = reactions[comment.commentId];
        acc[comment.commentId] = {
            type: 'COMMENT_READY',
            comment,
            commentId: comment.commentId,
            reactions: {
                total: commentReactions?.total ?? 0,
                reactions: commentReactions?.reactions ?? [],
                type: 'IDLE',
            },
            marked: { type: 'IDLE' },
            votes: getVotesStateIN(comment, votes),
        };
        comments.replies[comment.commentId].replies.forEach((reply) => {
            const replyReactions = reactions[reply.commentId];
            acc[reply.commentId] = {
                type: 'REPLY_READY',
                comment: reply,
                commentId: reply.commentId,
                parentId: comment.commentId,
                targetId: reply.replyTo.commentId,
                targetStatus: reply.replyTo.status,
                replyingToTargetLoading: false,
                reactions: {
                    total: replyReactions?.total ?? 0,
                    reactions: replyReactions?.reactions ?? [],
                    type: 'IDLE',
                },
                marked: { type: 'IDLE' },
            };
        });
        return acc;
    }, {});
    const replies = Object.keys(comments.replies).reduce((acc, commentId) => {
        const chunk = comments.replies[commentId];
        acc[commentId] = {
            parentCommentId: commentId,
            nextChunkState: { type: 'IDLE' },
            prevChunkState: { type: 'IDLE' },
            chunks: [
                {
                    type: 'INITIAL',
                    commentIds: (chunk.replies ?? []).map((c) => c.commentId),
                    nextCursor: chunk.pagingMetadata.nextCursor,
                    previousCursor: chunk.pagingMetadata.previousCursor,
                    total: chunk.pagingMetadata.count ?? 0,
                },
            ],
            newRepliesToReplies: {},
            liveRepliesPurgatory: [],
        };
        return acc;
    }, {});
    return [fetchedThunk, commentsById, replies];
};
const getFirstCommentId = (chunks) => {
    const firstChunk = first(chunks);
    if (!firstChunk) {
        return undefined;
    }
    switch (firstChunk.type) {
        case 'NEW': {
            return firstChunk.localOrCommentId;
        }
        case 'LIVE':
        case 'FETCHED': {
            return first(firstChunk.commentIds);
        }
        default:
            throw unreachable(firstChunk);
    }
};
const getLastCommentId = (chunks) => {
    const lastChunk = last(chunks);
    if (!lastChunk) {
        return undefined;
    }
    switch (lastChunk.type) {
        case 'NEW': {
            return lastChunk.localOrCommentId;
        }
        case 'LIVE':
        case 'FETCHED': {
            return last(lastChunk.commentIds);
        }
        default:
            throw unreachable(lastChunk);
    }
};
const getLastReplyId = (chunks) => {
    const lastChunk = last(chunks);
    if (!lastChunk) {
        return undefined;
    }
    switch (lastChunk.type) {
        case 'REPLY_TARGET':
            return lastChunk.commentId;
        case 'NEW':
            return lastChunk.localOrCommentId;
        case 'LIVE':
        case 'FETCHED':
        case 'INITIAL':
            return last(lastChunk.commentIds);
        default:
            throw unreachable(lastChunk);
    }
};
const getFirstReplyId = (chunks) => {
    const firstChunk = first(chunks);
    if (!firstChunk) {
        return undefined;
    }
    switch (firstChunk.type) {
        case 'REPLY_TARGET':
            return firstChunk.commentId;
        case 'NEW':
            return firstChunk.localOrCommentId;
        case 'FETCHED':
        case 'LIVE':
        case 'INITIAL':
            return firstChunk.commentIds[0];
        default:
            throw unreachable(firstChunk);
    }
};
const mergeWithNewComments = (commentsById, newComments) => {
    return uniq([...keys(commentsById), ...keys(newComments)]).reduce((acc, id) => {
        const newCommentState = newComments[id];
        const oldCommentState = commentsById[id];
        if (!newCommentState) {
            acc[id] = oldCommentState;
        }
        else if (!oldCommentState) {
            acc[id] = newCommentState;
        }
        else if (oldCommentState.type === 'REPLY_LIVE_PENDING' &&
            newCommentState.type === 'REPLY_READY') {
            // merge for case 'ONLY_LOAD_MORE_AND_LIVE',
            acc[id] = {
                ...newCommentState,
                isLive: true,
            };
        }
        else if ('localId' in oldCommentState &&
            oldCommentState.localId &&
            isReplyStateIN(newCommentState) &&
            newCommentState.type !== 'REPLY_LIVE_PENDING') {
            // We don't want to drop current localId of existing comments if they are fetched
            acc[id] = {
                ...newCommentState,
                localId: oldCommentState.localId,
            };
        }
        else {
            acc[id] = newCommentState;
        }
        return acc;
    }, {});
};
const mergeWithNewReplies = (replies, newReplies) => {
    // We don't want to drop current replieStates with new ones
    return {
        ...newReplies,
        ...replies,
    };
};
//# sourceMappingURL=comments-paging-reducer.js.map